import { LandingTrackingSettingsEntity } from "../Objects/Landing/LandingTrackingSettingsEntity"
import { appendTrackingScript, normalizeTrackingId } from "./trackingUtils"

let trackingConfig: LandingTrackingSettingsEntity | null = null

let initialized = false

type MarketingEventData = Record<string, string | number | boolean | null | undefined>

type TrackingWindow = Window & {
  dataLayer?: unknown[]
  fbq?: (...args: unknown[]) => void
  gtag?: (...args: unknown[]) => void
}

const getTrackingWindow = () => window as TrackingWindow

const hasAnyTrackingId = (config: LandingTrackingSettingsEntity) =>
  [
    config.googleAnalyticsMeasurementId,
    config.googleAdsConversionId,
    config.googleTagManagerId,
    config.metaPixelId,
  ].some((item) => normalizeTrackingId(item))

export const initializeTracking = (config: LandingTrackingSettingsEntity) => {
  if (initialized) {
    trackingConfig = config
    return
  }

  trackingConfig = config

  if (!config.isEnabled || !hasAnyTrackingId(config)) {
    return
  }

  const gtmId = normalizeTrackingId(config.googleTagManagerId)

  const gaId = normalizeTrackingId(config.googleAnalyticsMeasurementId)

  const adsId = normalizeTrackingId(config.googleAdsConversionId)

  const metaId = normalizeTrackingId(config.metaPixelId)

  const trackingWindow = getTrackingWindow()

  trackingWindow.dataLayer = trackingWindow.dataLayer || []
  trackingWindow.gtag = (...args: unknown[]) => {
    trackingWindow.dataLayer?.push(args)
  }

  if (gtmId) {
    appendTrackingScript(
      "op-gtm-script",
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`,
      document.head,
    )
    trackingWindow.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" })
  }

  if (gaId || adsId) {
    appendTrackingScript(
      "op-ga-script",
      "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(gaId ?? adsId ?? ""),
      document.head,
    )
    appendTrackingScript(
      "op-ga-config",
      "",
      document.head,
      `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date());`,
    )

    if (gaId) {
      trackingWindow.gtag("config", gaId, { send_page_view: false })
    }

    if (adsId) {
      trackingWindow.gtag("config", adsId)
    }
  }

  if (metaId) {
    appendTrackingScript("op-meta-pixel-lib", "https://connect.facebook.net/en_US/fbevents.js", document.head)
    appendTrackingScript(
      "op-meta-pixel-init",
      "",
      document.head,
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');`,
    )
    trackingWindow.fbq?.("init", metaId)
  }

  initialized = true
}

const canTrack = () => Boolean(trackingConfig?.isEnabled)

export const trackPageView = (path: string, title: string) => {
  if (!canTrack()) {
    return
  }

  const config = trackingConfig!

  if (config.enableAutomaticPageView) {
    const gaId = normalizeTrackingId(config.googleAnalyticsMeasurementId)
    if (gaId) {
      getTrackingWindow().gtag?.("event", "page_view", {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
        send_to: gaId,
      })
    }

    const metaId = normalizeTrackingId(config.metaPixelId)
    if (metaId) {
      getTrackingWindow().fbq?.("trackSingle", metaId, "PageView")
    }
  }
}

export const trackMarketingEvent = (
  eventName: string,
  data?: MarketingEventData,
) => {
  if (!canTrack()) {
    return
  }

  const trackingWindow = getTrackingWindow()

  const googleTargetId = normalizeTrackingId(trackingConfig!.googleAnalyticsMeasurementId)

  trackingWindow.gtag?.("event", eventName, {
    ...(data || {}),
    ...(googleTargetId ? { send_to: googleTargetId } : {}),
  })

  const metaId = normalizeTrackingId(trackingConfig!.metaPixelId)

  if (metaId) {
    trackingWindow.fbq?.("trackSingleCustom", metaId, eventName, data || {})
  }
}

const createEventId = (eventName: string) => {
  const randomPart = Math.random().toString(36).slice(2, 10)

  return `${eventName}-${Date.now()}-${randomPart}`
}

export const trackStandardMarketingEvent = (
  metaEventName: "Lead" | "CompleteRegistration" | "Contact" | "ViewContent",
  data?: MarketingEventData,
  googleEventName?: string,
) => {
  if (!canTrack()) {
    return
  }

  const eventId = createEventId(metaEventName)

  const trackingWindow = getTrackingWindow()

  trackingWindow.gtag?.("event", googleEventName ?? metaEventName.toLowerCase(), {
    ...(data || {}),
    event_id: eventId,
    ...(normalizeTrackingId(trackingConfig!.googleAnalyticsMeasurementId)
      ? { send_to: normalizeTrackingId(trackingConfig!.googleAnalyticsMeasurementId) }
      : {}),
  })

  trackingWindow.dataLayer?.push({
    event: googleEventName ?? metaEventName,
    event_id: eventId,
    ...(data || {}),
  })

  const metaId = normalizeTrackingId(trackingConfig!.metaPixelId)

  if (metaId) {
    trackingWindow.fbq?.("trackSingle", metaId, metaEventName, data || {}, { eventID: eventId })
  }
}

export const trackLeadConversion = (data?: MarketingEventData) =>
  trackStandardMarketingEvent("Lead", data, "generate_lead")

export const trackCompleteRegistrationConversion = (data?: MarketingEventData) =>
  trackStandardMarketingEvent("CompleteRegistration", data, "sign_up")
